import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/** The one error envelope every API error carries (docs/api/error-codes.json): a stable code, its params, a trace id. */
export interface ApiError {
  /** HTTP status; 0 when the server could not be reached. */
  status: number;
  code: string;
  params: Record<string, unknown>;
  traceId: string | null;
  /** The final, user-facing text, localized by the backend (Accept-Language); null when the body carried none. */
  message: string | null;
}

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

/**
 * Reads the error envelope off an `HttpErrorResponse` (or an already-unwrapped body). Returns null when there is no
 * usable code - a network failure, a proxy error page, or anything that is not the envelope.
 */
export function parseApiError(err: unknown): ApiError | null {
  if (!err || typeof err !== 'object') return null;
  const isResponse = err instanceof HttpErrorResponse;
  const status = isResponse ? err.status : typeof (err as { status?: unknown }).status === 'number' ? (err as { status: number }).status : 0;
  const body = isResponse ? err.error : (err as { error?: unknown }).error ?? err;
  if (!body || typeof body !== 'object') return null;
  const { code, params, traceId, message } = body as { code?: unknown; params?: unknown; traceId?: unknown; message?: unknown };
  if (typeof code !== 'string' || !CODE_PATTERN.test(code)) return null;
  return {
    status,
    code,
    params: params && typeof params === 'object' && !Array.isArray(params) ? (params as Record<string, unknown>) : {},
    traceId: typeof traceId === 'string' && traceId ? traceId : null,
    message: typeof message === 'string' && message.trim() ? message : null,
  };
}

/** The stable code of an error, or null. Branch on this, never on message text. */
export function apiErrorCode(err: unknown): string | null {
  return parseApiError(err)?.code ?? null;
}

/**
 * The text of an API error: the envelope `message`, already localized and interpolated by the backend. Only when
 * there is none (unreachable server, a proxy page, an unreadable body) does it use the console's own client-side
 * fallback (`errors.UNKNOWN`, or `errors.UNKNOWN_WITH_REFERENCE` with the trace id when known). The code is for
 * behavior only, never for building text.
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  private readonly translate = inject(TranslateService);

  message(err: unknown): string {
    const parsed = parseApiError(err);
    if (parsed?.message) return parsed.message;
    const traceId = parsed?.traceId ?? null;
    return traceId
      ? this.translate.instant('errors.UNKNOWN_WITH_REFERENCE', { traceId })
      : this.translate.instant('errors.UNKNOWN');
  }
}
